import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import useUserStore from "@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import LoadingSpinner from "components/common/LoadingSpiner";
import NoData from "components/common/NoData";
import { useSnackbar } from "contexts/SnackbarContext";
import {
    acceptLessonRequest,
    cancelLessonRequest,
    fetchInstructorLessonRequest,
    fetchLessonRequest,
    getLessonRequestErrorMessage,
    rejectLessonRequest,
} from "api/lessonRequest";
import { startDirectMessageRoom } from "api/chat";
import { MESSAGING_ENABLED } from "constants/featureFlags";
import {
    LESSON_REQUEST_STATUS,
    LESSON_REQUEST_STATUS_LABEL,
    LESSON_REQUEST_STATUS_TONE,
    canCancelLessonRequest,
    canInstructorActOnRequest,
    formatLessonRequestDayOrder,
    formatPriceKRW,
    getAutoEndTime,
    getInstructorComment,
    getRequestTypeMeta,
    isInstructorUser,
    normalizeChangedFields,
    PREMIUM_FIXED_PRICE,
} from "constants/lessonRequest";
import Badge from "components/common/Badge";
import OpenedReservationLinks from "components/lessonRequest/OpenedReservationLinks";
import "./LessonRequest.scss";

const formatTime = (value) => (value ? String(value).slice(0, 5) : "");

const formatSchedule = (request) => {
    const date = request?.requested_date ? dayjs(request.requested_date).format("YYYY.MM.DD") : "날짜 미정";
    const range = [formatTime(request?.requested_start_time), formatTime(request?.requested_end_time)]
        .filter(Boolean)
        .join("-");
    return [date, range].filter(Boolean).join(" · ");
};

const getDetailTitle = (request, meta) => {
    if (request?.request_type === "SPECIAL" && request?.topic) return request.topic;
    return request?.request_type_display || meta.title;
};

const getDetailSubtitle = (request, meta) => {
    if (request?.request_type === "SPECIAL" && request?.topic) {
        return request?.request_type_display || meta.title;
    }
    return request?.lesson_product_name || request?.sport_name || "수업 요청";
};

// 거절 강사 표기: 이름(닉네임). 탈퇴 등으로 정보가 비면 "강사"로 표시한다.
const formatRejectionInstructor = (instructor) => {
    const name = instructor?.name || "강사";
    return instructor?.nickname ? `${name}(${instructor.nickname})` : name;
};

const getTopicLabel = (request) => {
    if (request?.request_type === "PREMIUM") return "요청 메모";
    if (request?.request_type === "SPECIAL") return "스페셜 트레이닝 제목";
    return "주제";
};

export default function LessonRequestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const { showSuccess, showError } = useSnackbar();
    const isInstructor = isInstructorUser(userInfo);

    const [request, setRequest] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [acceptMessage, setAcceptMessage] = useState("");
    const [capacity, setCapacity] = useState(1);
    // PREMIUM 가격은 FE 에서 PREMIUM_FIXED_PRICE 로 고정한다(강사 입력 불가). 상태로 두지 않는다.
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [rejectReason, setRejectReason] = useState("");

    const loadRequest = useCallback(async () => {
        if (!userInfo) return;
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const data = isInstructor
                ? await fetchInstructorLessonRequest(id)
                : await fetchLessonRequest(id);
            setRequest(data);
            setStartTime(formatTime(data?.requested_start_time));
            setEndTime(formatTime(data?.requested_end_time));
            setCapacity(Number(data?.participant_count) > 0 ? Number(data.participant_count) : 1);
        } catch (error) {
            setErrorMessage(getLessonRequestErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    }, [id, isInstructor, userInfo]);

    useEffect(() => {
        loadRequest();
    }, [loadRequest]);

    const meta = useMemo(() => getRequestTypeMeta(request?.request_type), [request]);
    const instructorComment = getInstructorComment(request);
    const dayOrderLabel = formatLessonRequestDayOrder(request);
    const isRejected = request?.status === LESSON_REQUEST_STATUS.REJECTED;
    const canStudentCancel = !isInstructor && canCancelLessonRequest(request);
    const canInstructorAct = isInstructor && canInstructorActOnRequest(request);

    const handleCancel = async () => {
        if (!window.confirm("대기중인 수업 요청을 취소할까요?")) return;
        setIsSubmitting(true);
        try {
            await cancelLessonRequest(id);
            showSuccess("수업 요청을 취소했어요.");
            loadRequest();
        } catch (error) {
            showError(getLessonRequestErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const isPremiumRequest = request?.request_type === "PREMIUM";

    // 요청자가 탈퇴하면 BE가 requester를 null로 내리므로 public_id 유무로 버튼을 게이트한다.
    const requesterPublicId = request?.requester?.public_id;
    const canMessageRequester = isInstructor && MESSAGING_ENABLED && Boolean(requesterPublicId);

    // 강사별 거절 사유 목록(BE#524: rejections[].instructor 미니카드|null(탈퇴) + reject_reason).
    // 학생 본인 상세 GET에서만, 요청이 REJECTED일 때만 채워진다. 비어 있으면 기존 단일 사유 fallback 렌더링을 유지한다.
    const rejections = Array.isArray(request?.rejections) ? request.rejections : [];
    const showRejectionList = !isInstructor && isRejected && rejections.length > 0;

    const handleStartMessage = async (targetPublicId) => {
        try {
            const room = await startDirectMessageRoom(targetPublicId);
            if (room?.id) {
                navigate(`/messages/${encodeURIComponent(room.id)}`);
                return;
            }
            showError("메시지방을 시작하지 못했습니다.");
        } catch {
            showError("메시지방을 시작하지 못했습니다.");
        }
    };

    // 강사가 시작 시간을 바꾸면 종료 시간을 시작 + 3시간으로 자동 채운다.
    // 이후 강사가 종료 입력을 직접 수정하면 그 값을 그대로 유지한다.
    const handleStartTimeChange = (event) => {
        const nextStart = event.target.value;
        setStartTime(nextStart);
        const autoEnd = getAutoEndTime(nextStart);
        if (autoEnd) setEndTime(autoEnd);
    };

    const requestedStart = formatTime(request?.requested_start_time);
    const requestedEnd = formatTime(request?.requested_end_time);
    const hasTimeChange =
        Boolean(requestedStart && requestedEnd && startTime && endTime) &&
        (startTime !== requestedStart || endTime !== requestedEnd);

    // BE changed_fields 를 실제 변경 항목만 담은 정규화 목록으로 변환한다.
    // 변경 안내는 오직 이 목록이 있을 때만 노출한다(opened_capacity 단독 fallback 없음).
    const changedFields = normalizeChangedFields(request);
    const showChangeNotice = changedFields.length > 0;

    const handleAccept = async () => {
        if (!startTime || !endTime) {
            showError("수업 시작·종료 시간을 확인해 주세요.");
            return;
        }
        setIsSubmitting(true);
        try {
            await acceptLessonRequest(id, {
                start_time: startTime,
                end_time: endTime,
                capacity: Math.max(1, Number(capacity) || 1),
                message: acceptMessage.trim(),
                ...(isPremiumRequest ? { price: PREMIUM_FIXED_PRICE } : {}),
            });
            showSuccess("요청을 수락했어요. 학생에게 안내가 전달돼요.");
            loadRequest();
        } catch (error) {
            showError(getLessonRequestErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            showError("거절 사유를 입력해 주세요.");
            return;
        }
        setIsSubmitting(true);
        try {
            await rejectLessonRequest(id, { reject_reason: rejectReason.trim() });
            showSuccess("요청을 거절했어요. 사유가 학생에게 전달돼요.");
            loadRequest();
        } catch (error) {
            showError(getLessonRequestErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!userInfo) {
        return <Navigate to="/login" replace />;
    }

    if (isLoading) {
        return (
            <div className="lesson-request-page">
                <SubHeader title="수업 요청 상세" />
                <LoadingSpinner />
            </div>
        );
    }

    if (errorMessage || !request) {
        return (
            <div className="lesson-request-page">
                <SubHeader title="수업 요청 상세" />
                <div className="lesson-request-state">
                    <NoData title="수업 요청을 불러오지 못했어요" desc={errorMessage || "요청 정보를 찾을 수 없어요."} />
                    <button type="button" className="lesson-request-primary" onClick={loadRequest}>다시 불러오기</button>
                </div>
            </div>
        );
    }

    return (
        <div className="lesson-request-page lesson-request-page--detail">
            <SubHeader title="수업 요청 상세" />
            <main className="lesson-request-detail">
                <header className="lesson-request-detail__head">
                    <div>
                        <strong>{getDetailTitle(request, meta)}</strong>
                        <small>{getDetailSubtitle(request, meta)}</small>
                    </div>
                    <Badge 
                        text={request?.status_display
                          || LESSON_REQUEST_STATUS_LABEL[request?.status]
                          || request?.status
                          || ""} 
                        type="soft" 
                        color={LESSON_REQUEST_STATUS_TONE[request?.status] }/>
                </header>

                <dl className="lesson-request-detail__list">
                    {isInstructor && (
                        <div className="lesson-request-detail__row--requester">
                            <dt>요청자</dt>
                            <dd className="lesson-request-detail__requester">
                                <span>{ `${request?.requester?.name}(${request?.requester?.nickname})`}</span>
                                {canMessageRequester && (
                                    <button
                                        type="button"
                                        className="lesson-request-detail__message-button"
                                        onClick={() => handleStartMessage(requesterPublicId)}
                                    >
                                        메시지 보내기
                                    </button>
                                )}
                            </dd>
                        </div>
                    )}
                    <div>
                        <dt>일정</dt>
                        <dd>{formatSchedule(request)}</dd>
                    </div>
                    <div>
                        <dt>장소</dt>
                        <dd>{request.location_name || "장소 미정"}</dd>
                    </div>
                    {dayOrderLabel && (
                        <div>
                            <dt>회차</dt>
                            <dd>{dayOrderLabel}</dd>
                        </div>
                    )}
                    {request.topic && request.request_type !== "SPECIAL" && (
                        <div>
                            <dt>{getTopicLabel(request)}</dt>
                            <dd>{request.topic}</dd>
                        </div>
                    )}
                    <div>
                        <dt>참여 인원</dt>
                        <dd>{request.participant_count ?? 1}명</dd>
                    </div>
                </dl>

                {request.requester_message && (
                    <section className="lesson-request-detail__message">
                        <h3>요청자 메시지</h3>
                        <p>{request.requester_message}</p>
                    </section>
                )}

                {showChangeNotice && (
                    <section className="lesson-request-detail__change-notice">
                        <h3>변경 안내</h3>
                        <p>요청 내용과 다르게 개설되었어요. 변경된 내용을 확인해 주세요.</p>
                        <ul className="lesson-request-detail__change-fields">
                            {changedFields.map((item) => {
                                const hasDiff = item.before !== null && item.after !== null;
                                return (
                                    <li key={item.key ?? item.label}>
                                        <span className="lesson-request-detail__change-label">{item.label}</span>
                                        {hasDiff && (
                                            <span className="lesson-request-detail__change-diff">
                                                {`${item.before} → ${item.after}`}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                )}

                {showRejectionList ? (
                    <section className="lesson-request-detail__message lesson-request-detail__message--danger">
                        <h3>강사 거절 사유</h3>
                        <ul className="lesson-request-detail__rejections">
                            {rejections.map((entry, index) => (
                                <li key={entry?.instructor?.public_id || index}>
                                    <div className="lesson-request-detail__rejection-head">
                                        <strong>{formatRejectionInstructor(entry?.instructor)}</strong>
                                        {MESSAGING_ENABLED && entry?.instructor?.public_id && (
                                            <button
                                                type="button"
                                                className="lesson-request-detail__message-button"
                                                onClick={() => handleStartMessage(entry.instructor.public_id)}
                                            >
                                                메시지 보내기
                                            </button>
                                        )}
                                    </div>
                                    {entry?.reject_reason && <p>{entry.reject_reason}</p>}
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : instructorComment && (
                    <section className={`lesson-request-detail__message lesson-request-detail__message--${isRejected ? "danger" : "brand"}`}>
                        <h3>{isRejected ? "강사 거절 사유" : "강사 메시지"}</h3>
                        <p>{instructorComment}</p>
                    </section>
                )}

                <OpenedReservationLinks request={request} className="lesson-request-detail__reservation" />

                {canStudentCancel && (
                    <section className="btn-wrap btn-bottom-fixed">
                        <button type="button" className="btn btn-full btn-large btn-purple500" disabled={isSubmitting} onClick={handleCancel}>
                            요청 취소
                        </button>
                    </section>
                )}

                {canInstructorAct && (
                    <section className="lesson-request-detail__instructor">
                        <div className="lesson-request-detail__accept">
                            <h3>요청 수락</h3>
                            <div className="lesson-request-detail__time-row">
                                <label className="lesson-request-detail__time-field lesson-request-detail__time-field--time">
                                    <span>시작</span>
                                    <input
                                        type="time"
                                        className="lesson-request-input"
                                        value={startTime}
                                        onChange={handleStartTimeChange}
                                    />
                                </label>
                                <label className="lesson-request-detail__time-field lesson-request-detail__time-field--time">
                                    <span>종료</span>
                                    <input
                                        type="time"
                                        className="lesson-request-input"
                                        value={endTime}
                                        onChange={(event) => setEndTime(event.target.value)}
                                    />
                                </label>
                                <label className="lesson-request-detail__time-field lesson-request-detail__time-field--capacity">
                                    <span>정원</span>
                                    <input
                                        type="number"
                                        min="1"
                                        className="lesson-request-input"
                                        value={capacity}
                                        onChange={(event) => setCapacity(event.target.value)}
                                    />
                                </label>
                            </div>
                            {hasTimeChange && (
                                <div className="lesson-request-detail__time-warning" role="alert">
                                    <p>요청 시간과 다르게 개설됩니다. 수강생에게 변경된 시간이 안내됩니다.</p>
                                    <dl>
                                        <div>
                                            <dt>요청 시간</dt>
                                            <dd>{`${requestedStart}–${requestedEnd}`}</dd>
                                        </div>
                                        <div>
                                            <dt>개설 시간</dt>
                                            <dd>{`${startTime}–${endTime}`}</dd>
                                        </div>
                                    </dl>
                                </div>
                            )}
                            {isPremiumRequest && (
                                <p className="lesson-request-detail__hint">
                                    프리미엄 트레이닝은 {formatPriceKRW(PREMIUM_FIXED_PRICE)} 고정가 유료 연습반으로 개설돼요.
                                </p>
                            )}
                            <textarea
                                className="lesson-request-message-textarea"
                                maxLength="200"
                                value={acceptMessage}
                                onChange={(event) => setAcceptMessage(event.target.value)}
                                placeholder="학생에게 전할 안내 메시지 (예: 이론부터 천천히 진행할게요.)"
                                aria-label="수락 메시지"
                            />
                            <button type="button" className="lesson-request-primary" disabled={isSubmitting} onClick={handleAccept}>
                                수락하고 수업 개설
                            </button>
                        </div>

                        <div className="lesson-request-detail__reject">
                            <h3>요청 거절</h3>
                            <textarea
                                className="lesson-request-message-textarea"
                                maxLength="200"
                                value={rejectReason}
                                onChange={(event) => setRejectReason(event.target.value)}
                                placeholder="거절 사유를 입력해 주세요. (학생에게 그대로 전달돼요.)"
                                aria-label="거절 사유"
                            />
                            <button type="button" className="lesson-request-secondary" disabled={isSubmitting} onClick={handleReject}>
                                요청 거절
                            </button>
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
